import { useMemo } from "react";
import classNames from "classnames";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import type { FormValues } from "@/widgets/classic-form/form";
import { useFormStore, useYearRangeToggle } from "@/widgets/classic-form/form";
import { FieldComposer, fieldToConfig } from "@/features/form-fields";
import { type EventAssertion, type Field as FieldMeta } from "@/entities/form-metadata";
import type { PrefixObjectAccessor } from "@/shared/forms";
import { YearRangeToggle } from "./YearRangeToggle";
import styles from "./YearRangeInput.module.scss";

interface YearRangeInputProps {
    fieldData: FieldMeta<EventAssertion>;
    fieldPath: string;
    fieldsetLabel?: string;
    inputProperties?: React.InputHTMLAttributes<HTMLInputElement>;
}

export const YearRangeInput: React.FC<YearRangeInputProps> = ({
    fieldData,
    fieldPath,
    fieldsetLabel,
    inputProperties,
}) => {
    const { t } = useTranslation();
    const { isRangeMode, toggle } = useYearRangeToggle(fieldPath);

    const yearPath = `${fieldPath}.date.year` as PrefixObjectAccessor<FormValues, []>;
    const endYearPath = `${fieldPath}.date.endYear` as PrefixObjectAccessor<FormValues, []>;
    const proximityPath = `${fieldPath}.dateProximity.year` as PrefixObjectAccessor<FormValues, []>;

    // Subscribe to start year so the end year validation re-attaches when it changes
    const startYearValue = useFormStore(({ values }) => get(values, yearPath) as number | null);

    const endYearValidation = useMemo(
        () => ({
            test: (value: string) => {
                if (!value) return true;
                if (!startYearValue) return true;
                return Number(value) >= startYearValue;
            },
            message: t("yearRange.validation.startAfterEnd"),
        }),
        [startYearValue, t],
    );

    const startYearConfig = useMemo(() => {
        const label = isRangeMode ? t("yearRange.startYear") : fieldData.label;
        return { ...fieldToConfig({ ...fieldData, label }) };
    }, [fieldData, t, isRangeMode]);

    const endYearConfig = useMemo(
        () => ({ ...fieldToConfig(fieldData), label: t("yearRange.endYear") }),
        [fieldData, t],
    );

    return (
        <div
            className={classNames(styles.root, {
                [styles.singleMode!]: !isRangeMode,
                [styles.rangeMode!]: isRangeMode,
            })}
        >
            <div aria-live="polite" aria-atomic="true" className="hideVisually">
                {isRangeMode && t("yearRange.rangeModeEnabled")}
                {!isRangeMode && t("yearRange.singleModeEnabled")}
            </div>
            <FieldComposer fieldPath={yearPath} exactnessFieldPath={proximityPath} fieldConfig={startYearConfig}>
                <div>
                    <FieldComposer.Label />
                    <FieldComposer.FieldConnector>
                        {({ formField }, id) => (
                            <FieldComposer.Inputs.Year
                                {...inputProperties}
                                {...formField}
                                id={id}
                                {...(isRangeMode && {
                                    "aria-label": t("yearRange.startYearAria", { label: fieldsetLabel }),
                                })}
                            />
                        )}
                    </FieldComposer.FieldConnector>
                </div>
                {!isRangeMode && (
                    <div className={styles.exactness}>
                        <FieldComposer.GenericExactness />
                    </div>
                )}
            </FieldComposer>
            {isRangeMode && <span className={styles.separator} aria-hidden="true" />}
            {isRangeMode && (
                <div>
                    <FieldComposer fieldPath={endYearPath} fieldConfig={endYearConfig}>
                        <FieldComposer.Label />
                        <FieldComposer.FieldConnector>
                            {({ formField }, id) => (
                                <FieldComposer.Inputs.Year
                                    {...inputProperties}
                                    {...formField}
                                    id={id}
                                    aria-label={t("yearRange.endYearAria", { label: fieldsetLabel })}
                                    customValidation={endYearValidation}
                                />
                            )}
                        </FieldComposer.FieldConnector>
                    </FieldComposer>
                </div>
            )}
            <YearRangeToggle
                className={styles.toggle}
                isRangeMode={isRangeMode}
                onToggle={toggle}
                testId={`yearRange-toggle-${fieldPath}`}
            />
        </div>
    );
};
